import React, { useState, useEffect, useRef } from "react";
import { Menu, Button } from "@showitinc/showit-wc";
import cx from "classnames";
import styles from "./ContextMenuWrapper.module.css";
import AppConstants from "../../constants/AppConstants";
import ContextButton from "./ContextButton";

const { UI_BUTTON_SIZE_SMALL, PAGE_PANEL_STATE_DEFAULT } = AppConstants;

/**
 * Context Menu Wrapper
 * Menu wrapper for Page Panel Category and Item dropdown menus
 * @component
 * @param {object} props
 * @param {string} props.icon - string name for menu button icon
 * @param {string} props.state - string name for menu button state, default or distant
 * @param {string} props.size - string name for menu button size, small or default
 * @param {boolean} props.active - boolean for menu button active state
 * @param {React.ReactNode} props.children - Menu items and separators
 * @param {string} props.shift - string name for menu button shift, right or left
 */
const ContextMenuWrapper = ({ icon, children, state, size, onClick, active, shift = "right" }) => {
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const menuRef = useRef(null);

  const buttonClass = cx({
    [styles.default]: state === PAGE_PANEL_STATE_DEFAULT,
    [styles.small]: size === UI_BUTTON_SIZE_SMALL,
    [styles.active]: active,
  });

  useEffect(() => {
    const menu = menuRef.current;
    if (!(isMenuVisible && menu)) return undefined;

    let cancelled = false;
    let detach = () => {};

    (async () => {
      await menu.updateComplete;
      await new Promise((resolve) => requestAnimationFrame(resolve));
      if (cancelled || !menu.isConnected) return;

      menu.showPopover();

      const handleClose = () => setIsMenuVisible(false);
      const handleMouseOver = (e) => {
        const path = e.composedPath();
        const hovered = path.find(
          (el) => el.tagName && el.tagName.toLowerCase() === "si-menu-submenu",
        );
        const submenus = menu.querySelectorAll("si-menu-submenu");
        submenus.forEach((sub) => {
          if (sub !== hovered && sub.isOpen) {
            const content = sub.shadowRoot?.querySelector(".submenu-content");
            content?.hidePopover();
            sub.isOpen = false;
          }
        });
      };

      menu.addEventListener("si-hide", handleClose);
      menu.addEventListener("si-close", handleClose);
      menu.addEventListener("mouseover", handleMouseOver);

      detach = () => {
        menu.removeEventListener("si-hide", handleClose);
        menu.removeEventListener("si-close", handleClose);
        menu.removeEventListener("mouseover", handleMouseOver);
      };

      if (cancelled) detach();
    })();

    return () => {
      cancelled = true;
      detach();
    };
  }, [isMenuVisible]);

  const handleButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMenuVisible(true);
    onClick?.(e);
  };

  if (!isMenuVisible) {
    return (
      <ContextButton
        icon-prefix={icon}
        className={buttonClass}
        onClick={handleButtonClick}
        data-testid={`context-menu-button-${icon}`}
        data-context-menu-trigger
      />
    );
  }

  return (
    <Menu ref={menuRef} shift={shift} emitToggle>
      <ContextButton
        className={buttonClass}
        icon-prefix={icon}
        onClick={handleButtonClick}
        slot="trigger-element"
        data-context-menu-trigger
      />
      {children}
    </Menu>
  );
};

export default ContextMenuWrapper;
